import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import logoAroan from "@/assets/logo_aroan.webp";
import { useLocation, useNavigate } from "react-router-dom";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { 
  CheckCircle, Shield, Truck, Wrench, 
  ShieldAlert, Factory, GitBranch, ClipboardCheck,
  Menu, ChevronDown
} from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import DemoScheduler from "./DemoScheduler";
import { track } from "@/lib/track";

const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [applicationsOpen, setApplicationsOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  const applications = [
    { titleKey: "applications.quality.title", href: "/aplicacoes/qualidade", icon: CheckCircle, descKey: "applications.quality.description" },
    { titleKey: "applications.healthSafety.title", href: "/aplicacoes/saude-seguranca", icon: Shield, descKey: "applications.healthSafety.description" },
    { titleKey: "applications.logistics.title", href: "/aplicacoes/logistica", icon: Truck, descKey: "applications.logistics.description" },
    { titleKey: "applications.maintenance.title", href: "/aplicacoes/manutencao", icon: Wrench, descKey: "applications.maintenance.description" },
    { titleKey: "applications.lossPrevention.title", href: "/aplicacoes/prevencao-perdas", icon: ShieldAlert, descKey: "applications.lossPrevention.description" },
    { titleKey: "applications.production.title", href: "/aplicacoes/producao", icon: Factory, descKey: "applications.production.description" },
    { titleKey: "applications.processManagement.title", href: "/aplicacoes/gestao-processos", icon: GitBranch, descKey: "applications.processManagement.description" },
    { titleKey: "applications.audits.title", href: "/aplicacoes/auditorias", icon: ClipboardCheck, descKey: "applications.audits.description" },
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    setMobileMenuOpen(false);

    if (location.pathname === '/') {
      const element = document.querySelector(targetId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    } else {
      navigate(`/${targetId}`);
    }
  };

  const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const handleMobileNavigation = (href: string) => {
    setMobileMenuOpen(false);
    navigate(href);
  };

  const handleCTAClick = () => {
    track("cta_click", { location: mobileMenuOpen ? "header_mobile" : "header" });
    setMobileMenuOpen(false);
    setDemoOpen(true);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          <a href="/" onClick={handleLogoClick} className="flex items-center gap-2 cursor-pointer">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white">
              <img
                src={logoAroan}
                alt="Logo Aroan"
                className="w-7 h-7 object-contain"
                style={{ background: "white", borderRadius: "0.5rem" }}
              />
            </div>
            <span className="text-xl font-semibold">Aroan</span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            <a href="#solucao" onClick={e => handleNavClick(e, '#solucao')} className="text-foreground hover:text-primary transition-colors">
              {t('header.resources')}
            </a>
            
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent hover:bg-transparent data-[state=open]:bg-transparent text-foreground hover:text-primary h-auto p-0 font-normal">
                    {t('header.applications')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-1 p-4 md:w-[500px] md:grid-cols-2 bg-background border border-border rounded-lg shadow-lg">
                      {applications.map((app) => {
                        const Icon = app.icon;
                        return (
                          <li key={app.href}>
                            <NavigationMenuLink asChild>
                              <a
                                href={app.href}
                                onClick={(e) => { e.preventDefault(); navigate(app.href); }}
                                className="flex items-start gap-3 p-3 rounded-md hover:bg-muted transition-colors group"
                              >
                                <Icon className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                                <div className="flex flex-col">
                                  <span className="font-medium text-foreground">{t(app.titleKey)}</span>
                                  <span className="text-xs text-muted-foreground">{t(app.descKey)}</span>
                                </div>
                              </a>
                            </NavigationMenuLink>
                          </li>
                        );
                      })}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <a href="/como-funciona" onClick={(e) => { e.preventDefault(); navigate('/como-funciona'); }} className="text-foreground hover:text-primary transition-colors">
              {t('header.howItWorks')}
            </a>
            <a href="#resultados" onClick={e => handleNavClick(e, '#resultados')} className="text-foreground hover:text-primary transition-colors">
              {t('header.testimonials')}
            </a>
            <a href="/materiais" onClick={(e) => { e.preventDefault(); navigate('/materiais'); }} className="text-foreground hover:text-primary transition-colors">
              {t('header.materials')}
            </a>
            <a href="/blog" onClick={(e) => { e.preventDefault(); navigate('/blog'); }} className="text-foreground hover:text-primary transition-colors">
              {t('header.blog')}
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Button
              size="default"
              className="hidden sm:flex"
              onClick={handleCTAClick}
            >
              {t('header.cta')}
            </Button>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Abrir menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px]">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white">
                      <img
                        src={logoAroan}
                        alt="Logo Aroan"
                        className="w-7 h-7 object-contain"
                        style={{ background: "white", borderRadius: "0.5rem" }}
                      />
                    </div>
                    Aroan
                  </SheetTitle>
                </SheetHeader>
                
                <nav className="flex flex-col gap-2 mt-8">
                  <LanguageSwitcher variant="mobile" />
                  
                  <a 
                    href="#solucao" 
                    onClick={e => handleNavClick(e, '#solucao')} 
                    className="flex items-center px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors"
                  >
                    {t('header.resources')}
                  </a>
                  
                  <Collapsible open={applicationsOpen} onOpenChange={setApplicationsOpen}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors">
                      {t('header.applications')}
                      <ChevronDown className={`h-4 w-4 transition-transform ${applicationsOpen ? 'rotate-180' : ''}`} />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-4">
                      {applications.map((app) => {
                        const Icon = app.icon;
                        return (
                          <button
                            key={app.href}
                            onClick={() => handleMobileNavigation(app.href)}
                            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-foreground hover:bg-muted rounded-lg transition-colors"
                          >
                            <Icon className="h-4 w-4 text-primary shrink-0" />
                            <span>{t(app.titleKey)}</span>
                          </button>
                        );
                      })}
                    </CollapsibleContent>
                  </Collapsible>

                  <button 
                    onClick={() => handleMobileNavigation('/como-funciona')}
                    className="flex items-center px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors text-left"
                  >
                    {t('header.howItWorks')}
                  </button>
                  
                  <a 
                    href="#resultados" 
                    onClick={e => handleNavClick(e, '#resultados')} 
                    className="flex items-center px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors"
                  >
                    {t('header.testimonials')}
                  </a>
                  
                  <button 
                    onClick={() => handleMobileNavigation('/materiais')}
                    className="flex items-center px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors text-left"
                  >
                    {t('header.materials')}
                  </button>
                  
                  <button 
                    onClick={() => handleMobileNavigation('/blog')}
                    className="flex items-center px-4 py-3 text-foreground hover:bg-muted rounded-lg transition-colors text-left"
                  >
                    {t('header.blog')}
                  </button>

                  <div className="mt-4 px-4">
                    <Button className="w-full" onClick={handleCTAClick}>
                      {t('header.cta')}
                    </Button>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      <DemoScheduler open={demoOpen} onOpenChange={setDemoOpen} source="header" />
    </header>
  );
};

export default Header;
